import get from "lodash/get";
import set from "lodash/set";
import { FormContext, JsonSchema, ValidationError } from "../models";

export const getErrors = (schema: JsonSchema, errorSchema: any) => {
  let errors: string[] = [];

  // Flatten the errors for all object properties into a single array for the object
  Object.keys(errorSchema).forEach(propertyName => {
    const propertyErrors = (errorSchema[propertyName] && (errorSchema[propertyName].__errors as string[])) || [];
    propertyErrors.forEach((propertyError: string) => {
      errors.push(propertyError);
    });
  });
  // If there are one or more "required" errors for this object's properties, replace them with a single "required"
  // error for the object itself...
  if (errors.some(error => error.indexOf("is required.") !== -1)) {
    errors = errors.filter(error => error.indexOf("is required.") === -1);
    // Ensure "required" error appears first
    errors.unshift(`${schema.title} is required.`);
  }

  return errors;
};

export const getValidationErrors = <T>(
  formData: T,
  schema: JsonSchema,
  formContext: FormContext,
  errors: ValidationError[],
) => {
  const validationErrors = errors.reduce((errorSchema: ValidationError[], error: ValidationError) => {
    // Ignore oneOf, anyOf and const errors that are generated upon assigning null to select-based inputs...
    // (TODO: We may need to modify this logic in the future)
    if (["oneOf", "anyOf", "const"].indexOf(error.name) !== -1) {
      return errorSchema;
    }

    const { propertySchema, isPropertyRequired, propertyValue } = getPropertyMetadataFromValidationError(
      error,
      schema,
      formData,
    );

    // If this is a pattern error, for which the value is null and will otherwise have a 'Field is required.'
    // message generated...
    if (error.name === "pattern" && formData && propertyValue === null) {
      // Swallow the error, since we only need to display the 'Field is required.' error.
    }
    // Otherwise, if this is a pattern errror...
    else if (error.name === "pattern") {
      error.message = "Please enter a value that matches the pattern in the help text.";
      errorSchema.push(error);
    }
    // Otherwise, if this is a type error, required error, or disallowed enum value error, then translate to a
    // standard 'Field is required.' error...
    // WORKAROUND: NOTE: the error.name === "required" condition was replaced with 'isPropertyRequired'
    // to prevent required errors from oneOf/anyOf schemas to pass through here...This is a problem in RYMS
    // where multiple TrainSimulation Operations schemas were used in a oneOf property and required properties
    // for all Combined operation schemas were throwing errors. Need to review how 'oneOf' is supposed to be used...
    else if (
      error.name === "type" ||
      error.name === "required" ||
      error.name === "minItems" ||
      (error.name === "enum" && error.params.allowedValues)
    ) {
      // If this is either a required object-based or an array item property...
      if (isPropertyRequired) {
        // Set any undefined values to null if the form is in 'submit' mode
        // Test this in the playground by doing the following steps:
        // 1. Save the empty form
        // 2. Observe all empty elements in the Hours property will have a 'required' error
        // 3. Add additional 'Hour' elements and observe that these additional elements do not have 'required' errors
        //    until it is blurred or form is submitted again...
        const isEmptyValue = formContext.submitValidationEnabled ? propertyValue == null : propertyValue === null;

        const multiSelectErrorSchemaPathRegex = /.*\/items\/enum$/;
        const multiSelectErrorSchemaPathMatches = multiSelectErrorSchemaPathRegex.exec(error.schemaPath);
        const isMultiSelect = error.name === "enum" && multiSelectErrorSchemaPathMatches;

        // If this is a type error due to the property explicitly being set to null (i.e. to indicate no value was
        // provided), or if this is a disallowed enum value error and the property is not null (i.e. which we
        // otherwise want caught solely by the type error), or if the current value resolves as an empty value and
        // this is not an enum-related validation error (which would otherwise double up on a "Field is required"
        // error here)...
        if (
          ((error.name === "type" && propertyValue === null) ||
            (error.name === "enum" && error.params.allowedValues && propertyValue !== null) ||
            (error.name !== "enum" && isEmptyValue)) &&
          !isMultiSelect
        ) {
          if (isEmptyValue) {
            const propertyFormDataPath = error.property.replace(/^\./, "");
            if (formData && propertyValue === undefined) {
              set(formData as any, propertyFormDataPath, propertySchema.type === "array" ? [] : null);
            }
          }

          error.message = propertySchema.title ? propertySchema.title + " is required." : "Field is required.";
          errorSchema.push(error);
        } else if (error.name === "minItems" && propertyValue.length < error.params.limit) {
          error.message = `${propertySchema.title || "Field"} is required (Min. ${error.params.limit} selection${
            error.params.limit === 1 ? "" : "s"
          }).`;
          errorSchema.push(error);
        }
      }
    }
    // Otherwise, leave the error formatting as-is...
    else {
      errorSchema.push(error);
    }

    return errorSchema;
  }, []);

  return validationErrors;
};

export const getPropertyMetadataFromValidationError = (error: ValidationError, schema: JsonSchema, formData: any) => {
  const property = error.property.substring(error.property.lastIndexOf(".") + 1);

  const propertyMatch = /([^[]+)(?:\[(\d+)])?/.exec(property);

  if (!propertyMatch) {
    throw Error(`Property ${error.property} is not in a supported format.`);
  }

  const propertyName = propertyMatch[1];

  const propertyValue = getPropertyValueFromRawPropertyPathAndFormData(error.property, formData);
  const { propertySchema, propertyParentSchema } = getSchemaFromRawSchemaPathAndPropertyNameAndFormData(
    error.schemaPath,
    propertyName,
    schema,
  );

  const isPropertyRequired = propertyParentSchema.required
    ? propertyParentSchema.required.indexOf(propertyName) !== -1
    : propertyParentSchema.type === "array" || false;

  const result = {
    propertyName,
    propertySchema,
    isPropertyRequired,
    propertyValue,
  };

  return result;
};

const getPropertyValueFromRawPropertyPathAndFormData = (rawPropertyPath: string, formData: any) => {
  const propertyFormDataPath = rawPropertyPath.replace(/^\./, "");
  const propertyValue = formData && get(formData, propertyFormDataPath);

  return propertyValue;
};

const getSchemaFromRawSchemaPathAndPropertyNameAndFormData = (
  rawSchemaPath: string,
  propertyName: string,
  schema: JsonSchema,
) => {
  let propertySchemaPath;
  let propertyParentSchemaPath = rawSchemaPath
    .substring(0, rawSchemaPath.lastIndexOf("/"))
    .replace(/^#/, "")
    .replace(/\//g, ".")
    .replace(/^\./, "");
  // If this is an array...
  if (propertyParentSchemaPath.endsWith(".items")) {
    const itemsSchema = get(schema, propertyParentSchemaPath) as JsonSchema;

    // Need to drill down further to obtain the propertySchemaPath if 'items' is of type 'object'...See bug #1139
    // Note: Only supporting 1 extra level of drill down at the moment...
    if (itemsSchema.type === "object") {
      propertySchemaPath = `${propertyParentSchemaPath}.properties.${propertyName}`;
    } else {
      propertySchemaPath = propertyParentSchemaPath;
      propertyParentSchemaPath = propertySchemaPath.substring(0, propertySchemaPath.lastIndexOf(".items"));
    }
  }
  // Otherwise, if this path ends in the property name, then our parent schema path is actually the property path...
  else if (propertyParentSchemaPath.endsWith(`properties.${propertyName}`)) {
    propertySchemaPath = propertyParentSchemaPath;
    propertyParentSchemaPath = propertySchemaPath
      .substring(0, propertySchemaPath.lastIndexOf(`properties.${propertyName}`))
      .replace(/\.$/, "");
  }
  // Otherwise, our parent schema path is in the correct format, but we need to resolve the property path...
  else {
    propertySchemaPath = `${propertyParentSchemaPath}.properties.${propertyName}`.replace(/^\./, "");
  }

  const propertySchema = propertySchemaPath ? (get(schema, propertySchemaPath) as JsonSchema) : schema;
  const propertyParentSchema = propertyParentSchemaPath
    ? (get(schema, propertyParentSchemaPath) as JsonSchema)
    : schema;

  return {
    propertySchema,
    propertyParentSchema,
  };
};

export const getEmptyValueValidationErrors = (schema: JsonSchema, formData: any, errors: any) => {
  // Expecting root type is an object...
  if (schema.type === "object") {
    return getEmptyValueValidationErrorsFromObject(schema, formData, errors);
  }
  throw Error("getEmptyValueValidationErrors is only supported for object types.");
};

const getEmptyValueValidationErrorsFromObject = (schema: JsonSchema, formData: any, errors: any) => {
  Object.keys(schema.properties!).forEach(propertyName => {
    const propertySchema = schema.properties![propertyName] as JsonSchema;
    // If this is an array...
    if (propertySchema.type === "array") {
      const arrayPropertySchema = propertySchema.items! as JsonSchema;
      // If there is a sub-array, don't do anything right now...
      if (arrayPropertySchema.type === "array") {
      }
      // Otherwise, if this is an object...
      else if (arrayPropertySchema.type === "object") {
        ((formData && formData[propertyName]) || []).forEach((item: any, index: number) => {
          errors[propertyName] = errors[propertyName] || [];
          errors[propertyName][index] = getEmptyValueValidationErrorsFromObject(
            arrayPropertySchema,
            item || {},
            (errors && errors[propertyName] && errors[propertyName][index]) || {},
          );
        });
      }
      // Otherwise, assume this must be a primitive...
      else {
        // If an array property is required but the value is undefined, then throw a field is required error...
        // Note: react-jsonschema-form will initialize an array of undefined elements if minItems is specified...
        // https://github.com/mozilla-services/react-jsonschema-form/blob/ca8b6126dc3bc44b02081819848c4e6ce1901c23/src/utils.js#L151
        if (
          schema.required &&
          schema.required.indexOf(propertyName) !== -1 &&
          typeof formData[propertyName] === "undefined"
        ) {
          errors[propertyName] = propertySchema.title
            ? `${propertySchema.title} ${
                propertySchema.minItems && propertySchema.minItems > 1 ? "are" : "is"
              } required.`
            : "Field is required.";
        }
        // Otherwise, loop through each array item and check for empty value validation errors...
        else {
          ((formData && formData[propertyName]) || []).forEach((item: any, index: number) => {
            errors[propertyName] = errors[propertyName] || [];
            errors[propertyName][index] = getEmptyValueValidationErrorsFromPrimitive(
              formData && formData[propertyName] && formData[propertyName][index],
              propertySchema.items,
              schema.required ? schema.required.indexOf(propertyName) !== -1 : false,
              errors && errors[propertyName] && errors[propertyName][index],
            );
          });
        }
      }
    }
    // Otherwise, if this is an object...
    else if (propertySchema.type === "object") {
      errors[propertyName] = getEmptyValueValidationErrorsFromObject(
        propertySchema,
        (formData && formData[propertyName]) || {},
        (errors && errors[propertyName]) || {},
      );
    }
    // Otherwise, assume this must be a primitive...
    else {
      errors[propertyName] = getEmptyValueValidationErrorsFromPrimitive(
        formData && formData[propertyName],
        propertySchema,
        schema.required ? schema.required.indexOf(propertyName) !== -1 : false,
        errors && errors[propertyName],
      );
    }
  });

  return errors;
};

const getEmptyValueValidationErrorsFromPrimitive = (
  formData: any,
  schemaProps: any,
  isRequired: boolean,
  error: any,
) => {
  if (
    (schemaProps.type === "string" || schemaProps.type === "number" || schemaProps.type === "boolean") &&
    typeof formData === "undefined" &&
    isRequired
  ) {
    error = `${schemaProps.title || "Field"} is required.`;
  }

  return error;
};

export const applyValidationErrors = (errorsToApply: any, errors: any) => {
  // No need to recurse if we are applying an error to a primitive type array item
  // i.e. An array of primitive elements
  if (typeof errorsToApply === "string") {
    errors.addError(errorsToApply);
  }
  // Otherwise, errorsToApply is an Object...
  else {
    Object.keys(errorsToApply).forEach(customValidationErrorPropertyPath => {
      if (Array.isArray(errorsToApply[customValidationErrorPropertyPath])) {
        errorsToApply[customValidationErrorPropertyPath].forEach((item: any, index: number) => {
          const nextErrorsToApply = errorsToApply[customValidationErrorPropertyPath][index];
          const nextPropertyErrors = get(errors, `${customValidationErrorPropertyPath}[${index}]`);

          if (nextErrorsToApply) {
            applyValidationErrors(nextErrorsToApply, nextPropertyErrors);
          }
        });
      } else if (typeof errorsToApply[customValidationErrorPropertyPath] === "object") {
        applyValidationErrors(
          errorsToApply[customValidationErrorPropertyPath],
          get(errors, customValidationErrorPropertyPath),
        );
      } else {
        const propertyErrors: any = get(errors, customValidationErrorPropertyPath);
        if (!!errorsToApply[customValidationErrorPropertyPath]) {
          propertyErrors.addError(errorsToApply[customValidationErrorPropertyPath]);
        }
      }
    });
  }
};
